import React, {
  useCallback, useEffect, useRef, useState, useMemo,
} from 'react';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import { TreeSelect as AntdTreeSelect, Badge } from 'antd';
import { TreeSelectProps as AntdTreeSelectProps } from 'antd/lib/tree-select';
import { RefSelectProps } from 'antd/lib/select';
import type { DataNode } from '@rc-component/tree-select/lib/interface';
import { CaretDownFilled } from '@ant-design/icons';
import { Checkbox } from 'components/Controls/Checkbox';
import { SelectPropsCommon, SelectOptionType } from './types';
import cn from './styles.module.scss';

export interface MultiSelectProps<
  ValueType = any[],
  OptionType extends SelectOptionType = SelectOptionType
> extends SelectPropsCommon,
  Omit<AntdTreeSelectProps<ValueType, OptionType>, 'treeData' | 'onChange' | 'onSelect'> {
  options?: OptionType[],
  onChange?: (value: ValueType) => void,
  showSelectAllOption?: boolean,
  multiple: true,
}

export function MultiSelect<
  ValueType = any[],
  OptionType extends SelectOptionType = SelectOptionType,
>(props: MultiSelectProps<ValueType, OptionType>) {
  const {
    options = [],
    value,
    placeholder,
    onChange,
    size,
    debounceTime,
    popupClassName,
    showSelectAllOption = false,
    showCheckedStrategy = AntdTreeSelect.SHOW_CHILD,
    popupRender,
    ...restProps
  } = props;

  const [selectValue, setSelectValue] = useState<ValueType | undefined>(value);
  const [searchValue, setSearchValue] = useState('');
  const ref = useRef<RefSelectProps>(null);

  useEffect(() => {
    setSelectValue(value);
  }, [value]);

  const handleSelectChange = useCallback(
    debounceTime
      ? debounce((newValue) => onChange && onChange(newValue), debounceTime)
      : onChange as any,
    [onChange],
  );

  const optionValues = useMemo(() => {
    const isLeaf = (item: SelectOptionType) => !item.options?.length;

    const isFullyChecked = (item: SelectOptionType): boolean => {
      if (item.disabled) {
        return false;
      }
      if (isLeaf(item)) {
        return true;
      }
      return item.options!.every((child) => child.disabled || isFullyChecked(child));
    };

    const collectValues = (items: SelectOptionType[]): SelectOptionType['value'][] =>
      items.flatMap((item) => {
        if (isLeaf(item)) {
          return item.disabled ? [] : [item.value];
        }

        if (showCheckedStrategy === AntdTreeSelect.SHOW_PARENT) {
          if (!item.disabled && isFullyChecked(item)) return [item.value];
          return collectValues(item.options!);
        }

        if (showCheckedStrategy === AntdTreeSelect.SHOW_ALL) {
          const childValues = collectValues(item.options!);
          const selfValue = (!item.disabled && isFullyChecked(item)) ? [item.value] : [];
          return [...childValues, ...selfValue];
        }

        // SHOW_CHILD (default): only leaf values
        return collectValues(item.options!);
      });

    return collectValues(options);
  }, [options, showCheckedStrategy]);

  const isAllOptionsSelected = useMemo(() => {
    if (Array.isArray(selectValue) && optionValues.length > 0) {
      return optionValues.every((optionValue) => selectValue.includes(optionValue));
    }
    return false;
  }, [optionValues, selectValue]);

  const isSomeOptionsSelected = Array.isArray(selectValue) && selectValue?.length > 0;

  const handleChange = (newValue: ValueType) => {
    setSelectValue(newValue);
    handleSelectChange(newValue);
  };

  const handleSelectAll = useCallback(() => {
    const selectedValues = isAllOptionsSelected ? [] : optionValues;
    setSelectValue(selectedValues as ValueType);
    handleSelectChange(selectedValues);
  }, [isAllOptionsSelected, optionValues, handleSelectChange]);

  const selectAllPopupRender = useCallback((menu: React.ReactElement) => {
    const renderedMenu = popupRender ? popupRender(menu) : menu;
    const label = isAllOptionsSelected ? 'Unselect All' : 'Select All';
    return (
      <>
        <div className={cn.selectAll}>
          <Checkbox
            checked={isAllOptionsSelected}
            indeterminate={!isAllOptionsSelected && isSomeOptionsSelected}
            disabled={optionValues.length === 0}
            onChange={handleSelectAll}
            classNames={{
              root: cn.checkboxRoot,
              label: cn.checkboxLabel,
            }}
          >
            <span title={label}>{label}</span>
          </Checkbox>
        </div>

        {renderedMenu}
      </>
    );
  }, [popupRender, isAllOptionsSelected, isSomeOptionsSelected, optionValues.length, handleSelectAll]);

  return (
    <AntdTreeSelect
      ref={ref}
      placeholder={placeholder}
      popupMatchSelectWidth={false}
      maxTagCount={0}
      maxTagPlaceholder={() => (
        <div className={classNames(cn.tag, { [cn.hideTag]: searchValue })}>
          <span>{placeholder}</span>
        </div>
      )}
      treeCheckable
      suffixIcon={
        <div className={cn.suffix}>
          <Badge size={size === 'large' ? 'medium' : size} color="var(--bc-color-primary)" count={(selectValue as ValueType[])?.length || 0} overflowCount={999} />
          <CaretDownFilled />
        </div>
      }
      showCheckedStrategy={showCheckedStrategy}
      searchValue={searchValue}
      onSearch={setSearchValue}
      treeNodeFilterProp="label"
      value={selectValue || undefined}
      treeData={options}
      popupRender={showSelectAllOption ? selectAllPopupRender : popupRender}
      showSearch
      fieldNames={{
        label: 'label',
        children: 'options',
      }}
      size={size}
      onChange={handleChange}
      {...restProps as Omit<AntdTreeSelectProps<ValueType, DataNode>, 'treeData' | 'onChange' | 'onSelect'>}
      classNames={{
        ...restProps.classNames,
        popup: {
          ...restProps.classNames?.popup,
          root: classNames(cn.treeDropdown, popupClassName),
          item: classNames(cn.treeItem, restProps.classNames?.popup?.item)
        }
      }}
    />
  );
}
